import { useState } from 'react';
import { useRouter } from 'next/router';
import { Button } from '~/components/common/button';
import { Translate, useTranslate } from '~/i18n';
import { CheckGreenIcon, ClockIconOutline, SaudiMapIcon, SpinnerAltIcon, TickMark } from '~/svgs';
import { BackArrowIcon, RefreshIcon } from '~/svgs/marketplace';
import { OnboardFilesForm } from './onboard-files-form';
import FilesUploadedModal from './files-uploaded-modal';

type OnboardLegalDocsType = {
   showPackageSelection: () => void;
   docsUploaded: () => void;
   isDomesticUser: boolean;
   isNusukUser: boolean;
   resetAccountType: () => void;
   showInReview?: boolean;
};

export const OnboardLegalDocs = ({
   showPackageSelection,
   docsUploaded,
   isDomesticUser,
   isNusukUser,
   resetAccountType,
   showInReview,
}: OnboardLegalDocsType) => {
   const router = useRouter();
   const { lang } = useTranslate();
   const [showUploadSuccess, setShowUploadSuccess] = useState<boolean>(false);
   const [showReviewState, setShowReviewState] = useState<boolean>(showInReview || false);
   const progressList = [
      {
         title: <Translate id="onboard:create account" />,
         description: <Translate id="onboard:create account description" />,
         state: 'done',
      },
      {
         title: <Translate id="onboard:select account type" />,
         description: <Translate id="onboard:select account type description" />,
         state: 'done',
      },
      {
         title: <Translate id="onboard:upload documents" />,
         description: <Translate id="onboard:upload documents description" />,
         state: showReviewState ? 'done' : 'in-progress',
      },
      {
         title: <Translate id="onboard:account pending review" />,
         description: <Translate id="onboard:account active description" />,
         state: 'in-progress',
      },
   ];

   return (
      <div className=" py-8 px-5 h-full">
         <div className="flex gap-10">
            <div className="flex-col w-full lg:w-3/4 gap-3">
               <div className="flex justify-between mb-8">
                  <div className="flex flex-col w-full lg:w-[350px] gap-0.5">
                     <div
                        className="flex items-center font-semibold ltr:text-right rtl:text-left text-grey-200 hover:cursor-pointer"
                        onClick={() => resetAccountType()}>
                        {!showReviewState ? (
                           <span className="ltr:mr-3 rtl:ml-3 ">
                              <BackArrowIcon width={15} height={15} className="rtl:rotate-180" />
                           </span>
                        ) : null}{' '}
                        <span className="text-xs">
                           <Translate
                              id={`onboard:${
                                 showReviewState ? 'get started' : 'back to get started'
                              }`}
                           />
                        </span>
                     </div>
                     <span className="font-semibold">
                        <Translate
                           id={`onboard:${
                              showReviewState
                                 ? 'your account is pending activation'
                                 : 'setup legal docs'
                           }`}
                        />
                     </span>
                     <span className="text-xs text-grey-200">
                        <Translate
                           id={`onboard:${
                              showReviewState
                                 ? 'your data is now being processed by STA'
                                 : 'setup legal docs description'
                           }`}
                        />
                     </span>
                  </div>
                  {isDomesticUser || isNusukUser ? (
                     <div className="flex flex-col gap-1">
                        <div className="flex gap-3 mb-2">
                           {isDomesticUser ? (
                              <div className="flex gap-2 text-primary items-center">
                                 <SaudiMapIcon width={22} height={22} />
                                 <span className="text-sm">
                                    <Translate id="onboard:tourism packages" />
                                 </span>
                              </div>
                           ) : null}
                           <div className="flex gap-2 text-primary items-center">
                              <img src="/images/dashboard/kaaba-icon.png" width={22} alt="kaaba" />
                              <span className="text-sm">
                                 <Translate id="onboard:umrah packages" />
                              </span>
                           </div>
                        </div>
                        <Button
                           className="!w-full flex gap-3 items-center"
                           theme="outline-primary"
                           onClick={() => {
                              showPackageSelection();
                           }}>
                           <RefreshIcon />
                           <span className="">
                              <Translate id="common:change" />
                           </span>
                        </Button>
                     </div>
                  ) : null}
               </div>
               {!showReviewState ? (
                  <OnboardFilesForm
                     fwdBtnHandler={() => {
                        setShowUploadSuccess(true);
                        docsUploaded();
                     }}
                     isDomesticUser={isDomesticUser}
                     isNusukUser={isNusukUser}
                  />
               ) : (
                  <div className="py-8 bg-white">
                     <div className="flex flex-col gap-4 w-full lg:w-3/4 px-8 pb-8">
                        <span className="bg-grey-600 w-[55px] h-[55px] flex justify-center items-center rounded border border-grey-500">
                           <SpinnerAltIcon
                              width={30}
                              height={30}
                              // className="animate-[spin_5s_linear_infinite]"
                           />
                        </span>
                        <span className="text-xl font-semibold">
                           <Translate id="onboard:your details are being reviewed by STA" />
                        </span>
                        <div className="flex items-center gap-2">
                           <ClockIconOutline width={20} className="[&>*]:fill-primary" />
                           <span className="text-sm text-primary">
                              <Translate id="onboard:approval wait time" values={{ count: '2' }} />
                           </span>
                        </div>
                     </div>
                     <hr />
                     <div className="flex justify-between items-center pt-8 px-8">
                        <div className="flex flex-col gap-3 w-2/3">
                           <h4 className="text-sm font-semibold">
                              <Translate id="onboard:what can I do in the meantime?" />
                           </h4>
                           <p className="text-sm text-grey-200">
                              <Translate id="onboard:approval wait time note" />
                           </p>
                        </div>

                        <Button
                           className="h-10 !text-sm font-semibold gap-3"
                           theme="primary"
                           onClick={() => {
                              router.push(`/${lang}/marketplace`);
                           }}>
                           <span>
                              <Translate id="onboard:explore marketplace" />
                           </span>
                        </Button>
                     </div>
                  </div>
               )}
            </div>

            <div className="hidden lg:flex flex-col gap-3">
               <div className="font-semibold">
                  <Translate id="onboard:your are nearly there" />
               </div>
               <div className="relative">
                  <div className="absolute border-r border-grey-300 h-3/4 mt-5 ltr:ml-4 rtl:mr-2"></div>
                  {progressList?.map((step, idx) => (
                     <div key={idx} className="flex items-center gap-4 mb-8">
                        <span className="-mt-4 z-50">
                           {step?.state !== 'done' ? (
                              <div className="w-8 h-8 rounded-full bg-gray-300 flex justify-center items-center">
                                 {step?.state === 'not-done' ? (
                                    <TickMark width={18} className="[&>g>path]:!fill-grey-200" />
                                 ) : (
                                    <SpinnerAltIcon width={18} height={18} />
                                 )}
                              </div>
                           ) : (
                              <CheckGreenIcon width={30} height={30} />
                           )}
                        </span>
                        <div className="flex flex-col">
                           <div
                              className={`my-2 text-sm font-semibold ${
                                 step?.state === 'done' ? 'text-primary' : ''
                              }`}>
                              {step?.title}
                           </div>
                           <div className="text-xs">{step?.description}</div>
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         </div>

         <FilesUploadedModal
            open={showUploadSuccess}
            setOpen={setShowUploadSuccess}
            setShowReviewState={setShowReviewState}
         />
      </div>
   );
};
